import React from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import lodashCompact from 'lodash/fp/compact';

import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';

import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';

import {
  UTILITY_USER_ROLE_PARTNER_ADMIN,
  UTILITY_USER_ROLE_PARTNER_ACCOUNTING,
  UTILITY_USER_ROLE_PARTNER_PROPERTY_MANAGER,
  UTILITY_USER_ROLE_VIEW_REVENUE,
  UTILITY_USER_STATUS_PENDING_SIGNUP,
} from 'common/utility/userRoles';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_RED_INVERTED,
  SR_BUTTON_THEME_ADOBE_BLUE,
} from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrSeparator from 'client/components/SrSeparator';
import SrDarkModal from 'client/components/SrDarkModal';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE_UTILITY_PROFIT,
} from 'client/components/SrFormikField';
import SrModalContent from 'client/components/SrModalContent';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import { SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP } from 'client/components/SrInfoOptionCheckbox/SrInfoOptionCheckbox';
import { isHomebuyerPartner } from 'common/utility/helpers';

const getFormFields = ({
  formValues = {},
  canAddOrEditTeamMembers,
  utilityPartnerList,
  utilityPartnersById,
  utilityPartner,
  onEditPartnerIds,
  showPasswordFields,
}) => {
  const canEditRoles = canAddOrEditTeamMembers;
  const canViewRevenue =
    (canEditRoles || formValues?.roles?.includes(UTILITY_USER_ROLE_VIEW_REVENUE)) &&
    utilityPartner.experienceConfiguration.detailedModeActive;

  const brandProduct = isHomebuyerPartner(utilityPartner) ? 'Utility Wallet' : 'Utility Profit';

  return [
    {
      id: 'utilityUserId',
      type: 'hidden',
    },
    {
      id: 'firstName',
      title: 'First Name',
      type: 'short_text',
      properties: {
        placeholder: 'type first name here...',
      },
      validations: ['required'],
    },
    {
      id: 'lastName',
      title: 'Last Name',
      type: 'short_text',
      properties: {
        placeholder: 'type last name here...',
      },
      validations: ['required'],
    },
    {
      id: 'phone',
      title: 'Phone',
      type: 'phone',
      properties: {
        placeholder: 'type phone here...',
      },
      validations: ['required', 'phone'],
    },
    {
      id: 'email',
      title: 'Email',
      type: 'email',
      properties: {
        placeholder: 'type email here...',
      },
      validations: ['required', 'email'],
    },
    {
      id: 'roles',
      type: 'info_checkbox_multi',
      title: '🔑 Permissions',
      subtitle: 'Admins can check on permissions for this user.',
      properties: {
        choices: lodashCompact([
          {
            value: UTILITY_USER_ROLE_PARTNER_ADMIN,
            label: 'Admin',
            subtitle: 'Can manage all settings and users.',
            disabled: !canEditRoles,
            theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
          },
          canViewRevenue && {
            value: UTILITY_USER_ROLE_VIEW_REVENUE,
            label: 'View Revenue',
            subtitle: `Can view revenue earned in the "Summary" section of ${brandProduct}.`,
            disabled: !canEditRoles,
            theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
          },
          utilityPartner.experienceConfiguration.detailedModeActive && {
            value: UTILITY_USER_ROLE_PARTNER_ACCOUNTING,
            label: 'Transfer Money',
            subtitle: `Ability to add/edit the bank account on file, and transfer money out of ${brandProduct}.`,
            disabled: !canEditRoles,
            theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
          },
          utilityPartnerList.length > 1 && {
            label: 'Switch Platforms',
            value: UTILITY_USER_ROLE_PARTNER_PROPERTY_MANAGER,
            disabled: !canEditRoles,
            theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
            subtitle: (
              <div>
                <SrText font="greycliff" size="14" weight="400" lineHeight="20px">
                  Some PMs have multiple companies using {brandProduct}. This is the ability to
                  switch between the multiple platforms that exist for your multiple companies in
                  {brandProduct}.
                </SrText>
                <br />
                <br />
                <SrText
                  font="greycliff"
                  size="14"
                  weight="700"
                  lineHeight="20px"
                  paddingBottom="8px"
                >
                  🏢 Platforms you can switch to
                  <SrText
                    font="greycliff"
                    size="14"
                    weight="700"
                    lineHeight="20px"
                    color="blue300"
                    onClick={!canEditRoles ? null : onEditPartnerIds}
                  >
                    {' - Edit'}
                  </SrText>
                </SrText>
                <br />
                {(formValues.utilityPartnerIds || []).map((utilityPartnerId) => {
                  const partner = utilityPartnersById[utilityPartnerId];

                  if (!partner) {
                    return null;
                  }

                  return (
                    <ul key={partner.id}>
                      <li>
                        <SrText
                          font="greycliff"
                          size="14"
                          weight="400"
                          lineHeight="20px"
                          paddingLeft="10px"
                        >
                          {partner.name}
                        </SrText>
                      </li>
                    </ul>
                  );
                })}
              </div>
            ),
          },
        ]),
      },
    },
    {
      id: 'utilityPartnerIds',
      type: 'hidden',
    },
    {
      id: 'password',
      type: showPasswordFields ? 'password' : 'hidden',
      title: 'Create new password:',
      properties: {
        placeholder: 'type password here...',
      },
      validations: [],
    },
    {
      id: 'confirmPassword',
      type: showPasswordFields ? 'password' : 'hidden',
      title: 'Confirm new password:',
      properties: {
        placeholder: 'confirm password here...',
      },
      validations: ['requiredIfNotEmpty:password', 'confirmPassword:password'],
    },
  ];
};

const SrUtilityProfitPartnerUpsertPartnerUserModal = ({
  utilityPartnerList,
  currentUtilityUser,
  utilityPartner,
  canAddOrEditTeamMembers,
  onUpdateUtilityPartnerIds,
  user,
  error,
  onDeleteUser,
  onResendInvite,
  onSave,
  onCancel,
  reloadItems,
}) => {
  const initialValues = {
    utilityUserId: user?.id,
    firstName: user?.firstName,
    lastName: user?.lastName,
    email: user?.email,
    phone: user?.phone,
    roles: user?.roles || [UTILITY_USER_ROLE_VIEW_REVENUE],
    utilityPartnerIds: user?.utilityPartners?.map(({ id }) => id) || [],
    password: '',
    confirmPassword: '',
  };

  const utilityPartnersById = utilityPartnerList.reduce((acc, partner) => {
    acc[partner.id] = partner;
    return acc;
  }, {});

  return (
    <>
      <SrDarkModal onClose={onCancel}>
        <SrModalContent style={{ width: 412, marginTop: 4 }}>
          <SrBox display="flex" alignItems="center" justifyContent="center" flexDirection="column">
            <SrText
              weight="800"
              font="greycliff"
              fontSize="25px"
              lineHeight="30px"
              letterSpacing="0.5"
            >
              {user ? 'Edit' : 'Add'} Team Member
            </SrText>
          </SrBox>
          <SrSeparator marginTop="20px" marginBottom="8px" />
          <SrBox display="flex" alignItems="center" justifyContent="center" flexDirection="column">
            <Formik
              initialValues={createInitialValues({
                formFields: getFormFields({
                  currentUtilityUser,
                  utilityPartnerList,
                  utilityPartnersById,
                  utilityPartner,
                }),
                defaultValues: initialValues,
              })}
              validate={(formValues) => {
                return createFormValidationFn(
                  getFormFields({
                    currentUtilityUser,
                    utilityPartnerList,
                    utilityPartnersById,
                    utilityPartner,
                    formValues,
                    canAddOrEditTeamMembers,
                    showPasswordFields: user?.lastVisitedAt,
                  }),
                )(formValues);
              }}
              onSubmit={onSave}
              render={(formikBag) => {
                const formFields = getFormFields({
                  currentUtilityUser,
                  canAddOrEditTeamMembers,
                  formValues: formikBag.values,
                  utilityPartnerList,
                  utilityPartnersById,
                  utilityUser: user,
                  utilityPartner,
                  showPasswordFields: user?.lastVisitedAt,
                  onEditPartnerIds: (event) => {
                    preventDefaultPropagation(event);

                    onUpdateUtilityPartnerIds({
                      utilityPartnerIds: formikBag.values.utilityPartnerIds,
                      onUpdateUtilityPartnerIds: (utilityPartnerIds) => {
                        formikBag.setFieldValue('utilityPartnerIds', utilityPartnerIds);
                      },
                    });
                  },
                });

                const canEditRoles =
                  canAddOrEditTeamMembers &&
                  currentUtilityUser.id !== formikBag.values.utilityUserId;

                return (
                  <Form style={{ width: '100%', maxWidth: '350px' }}>
                    <SrBox margin="22px 0">
                      {formFields.map((formField) => {
                        return (
                          <SrFormikField
                            key={formField.id}
                            formField={formField}
                            formikBag={formikBag}
                            className={SR_FIELD_THEME_ADOBE_UTILITY_PROFIT}
                          />
                        );
                      })}
                    </SrBox>
                    {error && (
                      <SrBox marginBottom="18px">
                        <SrErrorMessage>{error.message}</SrErrorMessage>
                      </SrBox>
                    )}
                    <SrBox className="submitButtonContainer">
                      <SrButton
                        type="submit"
                        theme={SR_BUTTON_THEME_ADOBE_GREEN}
                        disabled={!formikBag.isValid}
                        hasBoxShadow
                      >
                        {user ? 'Save' : 'Send Invite'}
                      </SrButton>
                      {user && user.status === UTILITY_USER_STATUS_PENDING_SIGNUP && (
                        <SrButton
                          theme={SR_BUTTON_THEME_ADOBE_BLUE}
                          type="button"
                          margin="12px 0px"
                          onClick={onResendInvite}
                          hasBoxShadow
                        >
                          Resend Invite
                        </SrButton>
                      )}
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_GREY}
                        type="button"
                        margin="12px 0px"
                        onClick={onCancel}
                        hasBoxShadow
                      >
                        Cancel
                      </SrButton>
                      {user && canEditRoles && currentUtilityUser.id !== user.id && (
                        <SrButton
                          theme={SR_BUTTON_THEME_ADOBE_RED_INVERTED}
                          onClick={() => onDeleteUser({ user, reloadItems })}
                        >
                          Delete User
                        </SrButton>
                      )}
                    </SrBox>
                  </Form>
                );
              }}
            />
          </SrBox>
        </SrModalContent>
      </SrDarkModal>
    </>
  );
};

SrUtilityProfitPartnerUpsertPartnerUserModal.propTypes = {
  utilityPartnerList: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentUtilityUser: PropTypes.object.isRequired,
  utilityPartner: PropTypes.object.isRequired,
  user: PropTypes.object,
  error: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  canAddOrEditTeamMembers: PropTypes.bool.isRequired,
  onUpdateUtilityPartnerIds: PropTypes.func.isRequired,
  onDeleteUser: PropTypes.func.isRequired,
  onResendInvite: PropTypes.func.isRequired,
  reloadItems: PropTypes.func.isRequired,
};

SrUtilityProfitPartnerUpsertPartnerUserModal.displayName =
  'SrUtilityProfitPartnerUpsertPartnerUserModal';

export default SrUtilityProfitPartnerUpsertPartnerUserModal;
